import { addDays, format, isWeekend } from 'date-fns';

/** One generated schedule entry, matching the bulk-create request's item shape. */
export interface ScheduleSeedEntry {
  date: string;
  employee_id?: string;
  contact_id?: string;
  child_present_from?: string;
  child_present_to?: string;
  contact_present_from?: string;
  contact_present_to?: string;
}

export interface BuildScheduleSeedEntriesParams {
  /** First candidate date. */
  start: Date;
  /** How many weekdays to generate (the caller clamps to its own bounds). */
  days: number;
  /** "yyyy-MM-dd" keys of closure/holiday dates to skip. */
  holidays: Set<string>;
  /** "yyyy-MM-dd" keys already scheduled, to skip (empty when seeding a new plan). */
  alreadyScheduled?: Set<string>;
  employeeId?: string;
  contactId?: string;
  childFrom?: string;
  childTo?: string;
  contactFrom?: string;
  contactTo?: string;
}

/**
 * Weekday-patterned schedule generation, shared by the Days tab's "Generate days"
 * control (ST-4) and the create dialog's optional seed step (ST-5/S21).
 *
 * Extracted so the two callers cannot drift: weekend skipping, closure/holiday
 * skipping and duplicate-date skipping live here once.
 *
 * Duplicate dates are skipped because `buildDayRows` keys by date — a second
 * entry for the same day would silently overwrite the first and vanish from the
 * grid.
 */
export function buildScheduleSeedEntries({
  start,
  days,
  holidays,
  alreadyScheduled,
  employeeId,
  contactId,
  childFrom,
  childTo,
  contactFrom,
  contactTo,
}: BuildScheduleSeedEntriesParams): ScheduleSeedEntry[] {
  const entries: ScheduleSeedEntry[] = [];
  if (days < 1) return entries;

  const scheduled = alreadyScheduled ?? new Set<string>();
  let current = start;
  // Bounded scan: a year of candidates is far more than any settling-in span,
  // and guarantees termination if every candidate is skipped.
  let guard = 0;

  while (entries.length < days && guard < 400) {
    const key = format(current, 'yyyy-MM-dd');
    if (!isWeekend(current) && !holidays.has(key) && !scheduled.has(key)) {
      entries.push({
        date: key,
        employee_id: employeeId,
        contact_id: contactId,
        child_present_from: childFrom,
        child_present_to: childTo,
        contact_present_from: contactFrom,
        contact_present_to: contactTo,
      });
    }
    current = addDays(current, 1);
    guard += 1;
  }

  return entries;
}
